/**
 * Recharts Line / Area implementation.
 *
 * Single series: pass `data: SeriesDatum[]` with one numeric key matching the
 * first entry in `seriesKeys`. Multi-series: pass several keys. Area variant
 * fills the region under each line with a 30% opacity gradient.
 *
 * Lifted from the existing UptimeTrendChart pattern; uses chartTheme('recharts').
 */

import {
  LineChart as RechartsLineChart,
  AreaChart as RechartsAreaChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { LineChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = LineChartProps & CommonChartProps;

export function RechartsLine({ type, data, xKey, seriesKeys, xLabel, yLabel }: Props): JSX.Element {
  const theme = chartTheme('recharts');
  const palette = theme.theme.categoricalPalette;
  const isArea = type === 'area';
  const Chart = isArea ? RechartsAreaChart : RechartsLineChart;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <Chart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.axis.stroke} />
        <XAxis dataKey={xKey} tick={theme.axis.tick} stroke={theme.axis.stroke} label={xLabel} />
        <YAxis tick={theme.axis.tick} stroke={theme.axis.stroke} label={yLabel} />
        <Tooltip
          content={({ active, payload, label }) =>
            active && payload && payload.length > 0 ? (
              <DrillAwareTooltip
                header={String(label ?? '')}
                rows={payload.map((p, i) => ({
                  color: (p.color as string) ?? palette[i % palette.length],
                  label: String(p.name ?? p.dataKey ?? ''),
                  value: (p.value as number) ?? 0,
                }))}
              />
            ) : null
          }
        />
        {seriesKeys.length > 1 && (
          <Legend wrapperStyle={{ fontFamily: theme.legend.fontFamily, fontSize: theme.legend.fontSize }} />
        )}
        {seriesKeys.map((key, i) => {
          const color = palette[i % palette.length];
          return isArea ? (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stroke={color}
              fill={color}
              fillOpacity={0.25}
              strokeWidth={2}
              dot={false}
            />
          ) : (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          );
        })}
      </Chart>
    </ResponsiveContainer>
  );
}
